import React from 'react';

export type CardOption = {
  value: string;
  label: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
};

interface CardSelectProps {
  options: CardOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  columns?: 'auto' | '2' | '3' | '4';
}

export function CardSelect({
  options,
  value,
  onChange,
  disabled,
  columns = 'auto',
}: CardSelectProps) {
  // Determine grid columns based on whether options have descriptions
  const hasDescriptions = options.some((opt) => opt.description);

  const gridClass =
    columns === 'auto'
      ? hasDescriptions
        ? 'grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3' // Wider cards for descriptions
        : options.length <= 2
          ? 'grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3' // 2 columns for Private/Business
          : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3' // More columns for many options
      : columns === '2'
        ? 'grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3'
        : columns === '3'
          ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3'
          : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3';

  return (
    <div className={gridClass}>
      {options.map((option) => {
        const isSelected = option.value === value;
        const hasIcon = !!option.icon;

        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={
              `flex ${hasDescriptions || options.length <= 2 ? 'flex-col items-center justify-center text-center' : 'items-center'} p-2.5 sm:p-3 md:p-4 rounded-lg border transition-colors ` +
              (isSelected
                ? 'border-[#E34290] ring-2 ring-[#E34290] bg-[#E34290]/5'
                : 'border-gray-200 dark:border-gray-700 hover:border-[#E34290]/60 hover:bg-gray-50 dark:hover:bg-gray-800') +
              (disabled ? ' opacity-60 cursor-not-allowed' : '')
            }
          >
            {hasIcon && (
              <option.icon
                className={`${hasDescriptions || options.length <= 2 ? 'w-5 h-5 mb-2' : 'w-4 h-4 mr-2 flex-shrink-0'} text-[#E34290]`}
              />
            )}
            <div className={hasDescriptions || options.length <= 2 ? '' : 'flex-1 min-w-0'}>
              <div className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 break-normal">
                {option.label}
              </div>
              {option.description && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {option.description}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
